import { CircleNotch } from "@phosphor-icons/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getApiErrorMessage, useRegenerateAccessCode } from "@/lib/api";

interface Props {
  householdId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Confirmation before issuing a new access code.
 *
 * This used to be a bare icon with a tooltip, which was fine while the code only
 * lived in the admin UI. Now it is printed on kit sheets, and regenerating
 * silently turns every sheet already on paper into a dead end: the QR still
 * scans, the exchange is rejected, and the household lands on the code form.
 * The copy has to say that out loud, because nothing else can.
 */
export function RegenerateCodeDialog({ householdId, open, onOpenChange }: Props) {
  const regenerateMutation = useRegenerateAccessCode();
  const [error, setError] = useState("");

  const handleRegenerate = async () => {
    setError("");
    try {
      await regenerateMutation.mutateAsync({ id: householdId });
      onOpenChange(false);
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to issue a new access code."));
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setError("");
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Issue a new access code?</DialogTitle>
          <DialogDescription>
            The current code stops working straight away. Any kit sheet already printed for this
            home will no longer scan, so it needs reprinting.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleRegenerate()}
            disabled={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? (
              <CircleNotch className="size-4 animate-spin" weight="light" />
            ) : (
              "Issue new code"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
